import { useEffect, useState } from "react";
import { ComposedChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getEePointSeries } from "@/lib/api";
import { CHART } from "@/lib/chartTheme";

export default function PointSeriesChart({ lat, lon, source = "landsat", testId = "point-series" }) {
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true); setSeries(null);
    getEePointSeries(lat, lon, source)
      .then((d) => { if (active) setSeries(d); })
      .catch(() => { if (active) setSeries([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [lat, lon, source]);

  if (loading) {
    return <div className="text-[11px] text-[#334155] animate-pulse mt-2" data-testid={`${testId}-loading`}>
      Cargando serie 2000–2024…</div>;
  }
  if (!series || series.length === 0) {
    return <div className="text-[11px] text-[#64748b] mt-2">Sin serie disponible para este punto.</div>;
  }

  return (
    <div data-testid={testId} className="mt-3 pt-3 border-t border-[#e5e7eb]">
      <div className="label-caps mb-1">Evolución 2000–2024</div>
      <div style={{ height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 5, right: 2, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gPtLst" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART.lst} stopOpacity={0.2} />
                <stop offset="100%" stopColor={CHART.lst} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gPtNdvi" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART.ndvi} stopOpacity={0.16} />
                <stop offset="100%" stopColor={CHART.ndvi} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 9, fill: "#64748b" }} interval={5}
              tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
            <YAxis yAxisId="lst" tick={{ fontSize: 9, fill: CHART.lst }} width={30}
              tickLine={false} axisLine={false} />
            <YAxis yAxisId="ndvi" orientation="right" domain={[0, 1]} tick={{ fontSize: 9, fill: CHART.ndvi }}
              width={24} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ background: "#f8faf9", border: "1px solid #e5e7eb", fontSize: 11, borderRadius: 6 }}
              labelStyle={{ color: "#94a3b8" }} />
            <Area yAxisId="lst" type="monotone" dataKey="lst" name="LST °C" stroke={CHART.lst}
              fill="url(#gPtLst)" dot={false} strokeWidth={1.8} connectNulls isAnimationActive={false}
              activeDot={{ r: 3, strokeWidth: 0 }} />
            <Area yAxisId="ndvi" type="monotone" dataKey="ndvi" name="NDVI" stroke={CHART.ndvi}
              fill="url(#gPtNdvi)" dot={false} strokeWidth={1.8} connectNulls isAnimationActive={false}
              activeDot={{ r: 3, strokeWidth: 0 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-1 text-[10px]">
        <span style={{ color: CHART.lst }}>— LST (°C)</span>
        <span style={{ color: CHART.ndvi }}>— NDVI</span>
      </div>
    </div>
  );
}
